import { useEffect, useState } from 'react';
import KickstartV2Shell from './components/KickstartV2Shell';
import KickstartHome from './pages/KickstartHome';
import LessonPage from './pages/LessonPage';
import BreakthroughPage from './pages/BreakthroughPage';
import BridgePage from './pages/BridgePage';
import KickstartV2SignIn from './pages/KickstartV2SignIn';
import { useAppAuth } from '@/lib/appAuth';

export type KickstartRoute =
  | { name: 'home' }
  | { name: 'lesson'; num: string }
  | { name: 'breakthrough' }
  | { name: 'bridge'; blueprintId: string };

// Resolve `/bridge/<id>` deep links (set by the Express bridge resume redirect or pasted
// directly). Pulled out so the entry path stays declarative and isn't recomputed every
// render. Resume hint is `?resume=1` (not surfaced to the page yet — Slice 2 V1 just opens
// the canonical bridge view).
function readBridgeRouteFromLocation(): KickstartRoute | null {
  if (typeof window === 'undefined') return null;
  const match = window.location.pathname.match(/^\/bridge\/([A-Za-z0-9_-]+)\/?$/);
  if (!match) return null;
  return { name: 'bridge', blueprintId: match[1] };
}

/**
 * KickstartV2App — Foundry Race Bib light Kickstart surface.
 *
 * Mounted at `?surface=kickstart-v2`. Cold Meta-ad traffic lands here.
 * Same Race Bib chrome as Marketing v2 + OS v2 — one brand end-to-end.
 *
 * In-surface routing via local state (no react-router). Three views:
 * home (lesson grid) → lesson (video + Lab Work) → breakthrough (unlock).
 *
 * Auth: signed-in builders see the lesson flow; signed-out builders see the
 * Foundry sign-in screen (Clerk email-code, mirroring legacy KickstartSignIn).
 * The TopBar still renders so the brand mark + cohort meta stay visible.
 */
export default function KickstartV2App() {
  const [route, setRoute] = useState<KickstartRoute>(
    () => readBridgeRouteFromLocation() ?? { name: 'home' },
  );
  const { isLoaded, isSignedIn } = useAppAuth();

  // Keep the in-app route in sync with browser navigation (back/forward, paste).
  useEffect(() => {
    function handlePop() {
      const next = readBridgeRouteFromLocation();
      if (next) setRoute(next);
      else setRoute((prev) => (prev.name === 'bridge' ? { name: 'home' } : prev));
    }
    window.addEventListener('popstate', handlePop);
    return () => window.removeEventListener('popstate', handlePop);
  }, []);

  // Wait for Clerk to settle before deciding which surface to show. Avoids a
  // flash of the sign-in screen for already-signed-in builders on first paint.
  if (!isLoaded) {
    return (
      <KickstartV2Shell>
        <div className="grid h-[60dvh] place-items-center">
          <div className="flex items-center gap-3">
            <span className="block h-2 w-2 animate-fd-pulse rounded-full bg-fd-accent" />
            <span className="font-geist-mono text-[11px] font-semibold uppercase tracking-[0.10em] text-fd-text-subtle">
              Loading…
            </span>
          </div>
        </div>
      </KickstartV2Shell>
    );
  }

  if (!isSignedIn) {
    return (
      <KickstartV2Shell>
        <KickstartV2SignIn />
      </KickstartV2Shell>
    );
  }

  // Push real `/bridge/:id` URLs so the address bar matches the in-surface state and the
  // Express resume redirect lands on the right place. Other routes keep `/` to avoid
  // surprising existing deep links.
  function navigate(next: KickstartRoute) {
    window.scrollTo(0, 0);
    setRoute(next);
    if (typeof window !== 'undefined') {
      const path = next.name === 'bridge' ? `/bridge/${next.blueprintId}` : '/';
      if (window.location.pathname !== path) {
        window.history.pushState({}, '', path + window.location.search);
      }
    }
  }

  return (
    <KickstartV2Shell onHomeClick={() => navigate({ name: 'home' })}>
      {route.name === 'home' && (
        <KickstartHome
          onOpenLesson={(num) => navigate({ name: 'lesson', num })}
          onOpenBreakthrough={() => navigate({ name: 'breakthrough' })}
        />
      )}
      {route.name === 'lesson' && (
        <LessonPage
          num={route.num}
          onBack={() => navigate({ name: 'home' })}
          onOpenLesson={(num) => navigate({ name: 'lesson', num })}
          onOpenBreakthrough={() => navigate({ name: 'breakthrough' })}
        />
      )}
      {route.name === 'breakthrough' && (
        <BreakthroughPage
          onBack={() => navigate({ name: 'home' })}
          onOpenBridge={(blueprintId) => navigate({ name: 'bridge', blueprintId })}
        />
      )}
      {route.name === 'bridge' && (
        <BridgePage
          blueprintId={route.blueprintId}
          onBack={() => navigate({ name: 'breakthrough' })}
        />
      )}
    </KickstartV2Shell>
  );
}
